/**
 * Product badge utilities — drives the Featured / Best Sellers / New Arrivals
 * / Trending corner badges on ProductCard. Reads product.collections (slug
 * array) + product.collection_expiries (slug -> ISO date) so admin-managed
 * expiry dates are respected without touching the rest of the product model.
 */

// Priority for stacking (lower = higher priority). Max 2 badges shown.
export const BADGE_PRIORITY = {
  featured: 1,
  "best-sellers": 2,
  "new-arrivals": 3,
  trending: 4,
};

// Visual spec generated by design_agent (Feb 2026).
// Tailwind classes kept inline so the bundler tree-shakes correctly.
export const BADGE_SPECS = {
  featured: {
    id: "featured",
    label: "Featured",
    icon: "★",
    className:
      "bg-[#FBBF24] text-[#111111] border border-[#F59E0B] flex items-center justify-center gap-1 rounded-[4px] px-1.5 py-0.5 md:px-2 md:py-1 text-[9px] md:text-[10px] font-bold tracking-widest uppercase shadow-sm whitespace-nowrap",
    shimmer: false,
  },
  "best-sellers": {
    id: "best-sellers",
    label: "Best Seller",
    icon: "🔥",
    className:
      "bg-gradient-to-r from-[#E11D48] to-[#BE123C] text-white flex items-center justify-center gap-1 rounded-[4px] px-1.5 py-0.5 md:px-2 md:py-1 text-[9px] md:text-[10px] font-bold tracking-widest uppercase shadow-[0_2px_8px_rgba(225,29,72,0.3)] whitespace-nowrap relative overflow-hidden",
    shimmer: true,
  },
  "new-arrivals": {
    id: "new-arrivals",
    label: "New",
    icon: "✨",
    className:
      "bg-[#7C3AED] text-white border border-[#6D28D9] flex items-center justify-center gap-1 rounded-[4px] px-1.5 py-0.5 md:px-2 md:py-1 text-[9px] md:text-[10px] font-bold tracking-widest uppercase shadow-sm whitespace-nowrap",
    shimmer: false,
  },
  trending: {
    id: "trending",
    label: "Trending",
    icon: "📈",
    className:
      "bg-[#059669] text-white border border-[#047857] flex items-center justify-center gap-1 rounded-[4px] px-1.5 py-0.5 md:px-2 md:py-1 text-[9px] md:text-[10px] font-bold tracking-widest uppercase shadow-sm whitespace-nowrap",
    shimmer: false,
  },
};

// Size variants — controls padding + text size on the badge chip.
export const BADGE_SIZE_CLASSES = {
  small: "px-1 py-0.5 md:px-1.5 md:py-0.5 text-[8px] md:text-[9px] gap-0.5",
  medium: "px-1.5 py-0.5 md:px-2 md:py-1 text-[9px] md:text-[10px] gap-1",
  large: "px-2 py-1 md:px-2.5 md:py-1.5 text-[10px] md:text-[11px] gap-1.5",
};

/**
 * Returns the highest-priority badges for a product, respecting per-badge
 * expiry dates AND admin Visual Settings (max cap, size, colour overrides).
 *
 *   getActiveBadges(product, { max_badges: 3, badge_size: "medium", badge_colors: {…} })
 */
export function getActiveBadges(product, visualSettings = {}) {
  if (!product) return [];
  const collections = product.collections || [];
  const expiries = product.collection_expiries || {};
  const now = Date.now();
  const max = Math.max(1, Math.min(4, visualSettings.max_badges || 3));
  const size = visualSettings.badge_size || "medium";
  const colorOverrides = visualSettings.badge_colors || {};

  const active = collections.filter((slug) => {
    if (!BADGE_SPECS[slug]) return false;
    const exp = expiries[slug];
    if (!exp) return true;
    const t = new Date(exp).getTime();
    if (Number.isNaN(t)) return true;
    return now < t;
  });

  return active
    .sort((a, b) => (BADGE_PRIORITY[a] || 99) - (BADGE_PRIORITY[b] || 99))
    .slice(0, max)
    .map((slug) => {
      const spec = BADGE_SPECS[slug];
      const override = colorOverrides[slug];
      // Replace size segment of className with chosen variant.
      const sizeCls = BADGE_SIZE_CLASSES[size] || BADGE_SIZE_CLASSES.medium;
      // Strip the default size classes from spec.className (they're hand-tuned medium)
      const baseCls = spec.className
        .replace(/px-1\.5 py-0\.5 md:px-2 md:py-1 /g, "")
        .replace(/text-\[9px\] md:text-\[10px\] /g, "")
        .replace(/gap-1 /g, "");
      return {
        ...spec,
        className: `${baseCls} ${sizeCls}`,
        styleOverride: override
          ? {
              backgroundImage: "none",
              background: override.bg || undefined,
              color: override.text || undefined,
              borderColor: override.border || undefined,
            }
          : null,
      };
    });
}

/**
 * Quick preset offsets used by the admin form when toggling a badge ON.
 * Each preset returns an ISO string (or empty string for Forever).
 */
export const EXPIRY_PRESETS = [
  { label: "7 days", days: 7 },
  { label: "14 days", days: 14 },
  { label: "30 days", days: 30 },
  { label: "60 days", days: 60 },
  { label: "90 days", days: 90 },
  { label: "Forever", days: null },
];

export function daysFromNowISO(days) {
  if (days == null) return "";
  const d = new Date();
  d.setDate(d.getDate() + days);
  // Strip time → midnight UTC so admin sees a clean YYYY-MM-DD in the input.
  return d.toISOString().slice(0, 10);
}

export function expiryHumanLabel(iso) {
  if (!iso) return "Forever";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const remain = Math.ceil((t - Date.now()) / (1000 * 60 * 60 * 24));
  if (remain <= 0) return "Expired";
  if (remain === 1) return "1 day left";
  if (remain <= 90) return `${remain} days left`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
